import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import { postData } from "../../../../utility/Utility";
import CustomLoader from "../../../../Components/CustomLoader/CustomLoader";

function PartnerList() {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const rowsPerPage = 10;

  const fetchPartners = async (page = 1, search = "") => {
    setLoading(true);
    try {
      const response = await postData("api/partners/get-all", {
        search: search,
        page: page,
        limit: rowsPerPage
      });

      
      if (response?.success) {
        setPartners(response.data || []);
        setTotalPages(Math.ceil(response.total / rowsPerPage));
      } else {
        toast.error(response?.message || "Failed to fetch partners");
      }
    } catch (error) {
      toast.error("Error fetching partners");
      console.error("Fetch partners error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners(currentPage + 1, searchTerm);
  }, [currentPage, searchTerm]);

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    fetchPartners(1, searchTerm);
  };

  const handleReset = () => {
    setSearchTerm("");
    setCurrentPage(0);
    fetchPartners(1);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await postData(`api/partners/delete/${id}`);
        if (response.success) {
          toast.success("Partner deleted successfully");
          fetchPartners(currentPage + 1, searchTerm);
        } else {
          toast.error(response.message || "Failed to delete partner");
        }
      } catch (error) {
        toast.error("Error deleting partner");
        console.error("Delete partner error:", error);
      }
    }
  };

  const handleStatusChange = async (partner) => {
    const newStatus = partner.status === "active" ? "inactive" : "active";

    const result = await Swal.fire({
      title: `Are you sure you want to ${newStatus === "active" ? "activate" : "deactivate"} this partner?`,
      text: `The partner will be ${newStatus === "active" ? "activated" : "deactivated"}.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, ${newStatus === "active" ? "activate" : "deactivate"} it!`,
    });

    if (result.isConfirmed) {
      try {
        const response = await postData(`api/partners/update/${partner._id}`, {
          status: newStatus,
          id: partner._id,
        });
        if (response.success) {
          const updatedPartners = partners.map(p => 
            p._id === partner._id ? {...p, status: newStatus} : p
          );
          setPartners(updatedPartners);
          toast.success(`Partner ${newStatus === "active" ? "activated" : "deactivated"} successfully`);
        } else {
          toast.error(response.message || `Failed to ${newStatus === "active" ? "activate" : "deactivate"} partner`);
        }
      } catch (error) {
        toast.error(`Error ${newStatus === "active" ? "activating" : "deactivating"} partner`);
        console.error("Status change error:", error);
      }
    }
  };

  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={3000} />
      <CustomLoader loading={loading} />
      <div className="content">
        <div className="container-fluid">
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <h4 className="fs-18 fw-semibold m-0">Insurance Partner Management</h4>
              <p className="fw-semibold my-2">Insurance Partner List</p>
            </div>
            <div>
              <button
                className="btn btn-primary"
                onClick={() => navigate("/add-partner")}
                disabled={loading}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm me-1"></span>
                ) : (
                  <i className="mdi mdi-plus-square me-1"></i>
                )}
                Add Partner
              </button>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row g-3 align-items-center">
                      <div className="col-md-12 col-lg-4">
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control ps-4"
                            placeholder="Search insurance partners..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <span className="input-group-text">
                            <i className="mdi mdi-magnify"></i>
                          </span>
                        </div>
                      </div>

                      <div className="col-md-12 col-lg-3 d-flex gap-2">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          {loading ? (
                            <span className="spinner-border spinner-border-sm me-1"></span>
                          ) : null}
                          Search
                        </button>

                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleReset}
                          disabled={loading}
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          <div className="row mt-2">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Insurance Company</th>
                            <th>Logo</th>
                            <th>Status</th>
                            <th>Created At</th>
                            <th width="150px">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {partners.length > 0 ? (
                            partners.map((partner, index) => (
                              <tr key={partner._id}>
                                <td>{currentPage * rowsPerPage + index + 1}</td>
                                <td>{partner.name}</td>
                                <td>
                                  <img 
                                    src={partner.image} 
                                    alt={partner.name}
                                    style={{ width: 50, height: 50, objectFit: 'contain' }} 
                                    className="img-thumbnail"
                                  />
                                </td>
                                <td>
                                  <span
                                    className={`badge text-capitalize bg-${partner.status === 'active' ? 'success' : 'danger'}`}
                                  >
                                    {partner.status || 'inactive'}
                                  </span>
                                </td>
                                <td>
                                  {new Date(partner.createdAt).toLocaleDateString()}
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() =>
                                        navigate(`/update-partner`, {
                                          state: { partnerData: partner },
                                        })
                                      }
                                    >
                                      <i className="mdi mdi-pencil"></i>
                                    </button>
                                    <button
                                      className={`btn btn-sm btn-outline-${partner.status === 'active' ? 'warning' : 'success'}`}
                                      onClick={() => handleStatusChange(partner)}
                                      title={partner.status === 'active' ? 'Deactivate' : 'Activate'}
                                    >
                                      <i className={`mdi mdi-${partner.status === 'active' ? 'close' : 'check'}`}></i>
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => handleDelete(partner._id)}
                                    >
                                      <i className="mdi mdi-delete"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="6" className="text-center py-4">
                                No insurance partners found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      {totalPages > 1 && (
                        <ReactPaginate
                          previousLabel={<i className="mdi mdi-chevron-left"></i>}
                          nextLabel={<i className="mdi mdi-chevron-right"></i>}
                          breakLabel="..."
                          breakClassName="page-item"
                          breakLinkClassName="page-link"
                          pageCount={totalPages}
                          marginPagesDisplayed={2}
                          pageRangeDisplayed={5}
                          onPageChange={handlePageChange}
                          containerClassName="pagination pagination-rounded justify-content-end mt-3"
                          pageClassName="page-item"
                          pageLinkClassName="page-link"
                          previousClassName="page-item"
                          previousLinkClassName="page-link"
                          nextClassName="page-item"
                          nextLinkClassName="page-link"
                          activeClassName="active"
                          forcePage={currentPage}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PartnerList;